import React from 'react';
import SiteNav from '../components/SiteNav';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';

const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };

const microEyebrow: React.CSSProperties = {
  ...mono,
  fontSize: '.6875rem',
  letterSpacing: '.18em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const WHY_NOW = [
  { n: '01', kicker: 'Exposed authority', body: 'Keys behind recurring approvals become long-lived targets for an attacker.' },
  { n: '02', kicker: 'Harvest-now', body: 'Adversaries can collect data today and decrypt it once quantum capability arrives.' },
  { n: '03', kicker: 'Control gap', body: "Custody, wallets, signing, and governance won't all migrate in one coordinated step." },
];

const STEPS = [
  {
    n: '01',
    title: 'Smart accounts enforce the control layer',
    body: 'Issuer-controlled smart accounts validate richer authorization policy without changing the network.',
  },
  {
    n: '02',
    title: 'Dual signatures authorize privileged actions',
    body: 'Operations clear only when the existing signature path and post-quantum path approve the same intent, call data, nonce, and policy.',
  },
  {
    n: '03',
    title: 'Single-use operator keys narrow the blast radius',
    body: 'Operator credentials become one-time signing material for a single operation — not reusable standing authority.',
  },
  {
    n: '04',
    title: 'Existing asset rails stay in place',
    body: 'Quantum-safe control logic deploys with no L1 fork, token migration, or re-issuance — existing controls remain intact.',
  },
];

const FLOW = [
  { idx: '01 / origin', title: 'Issuer / bank', body: 'Treasury, admin, and policy operations start at the institution.', foot: 'ECDSA + PQ intent', active: false },
  { idx: '02 / authorization', title: 'QSSN node', body: 'Dual-signature policy checks and vault-backed signing clear before any key is released.', foot: 'single-use operator key', active: true },
  { idx: '03 / validation', title: 'Smart account', body: 'The smart account validates the authorization while the asset contract stays intact.', foot: 'smart account validation', active: false },
  { idx: '04 / settlement', title: 'Counterparties', body: 'Wallets, venues, and rails continue over existing asset infrastructure.', foot: 'existing asset rails', active: false },
];

const h2Style: React.CSSProperties = {
  margin: 0,
  fontWeight: 500,
  fontSize: 'clamp(2rem,3.4vw,3.1rem)',
  lineHeight: 1.05,
  letterSpacing: '-.02em',
  color: 'var(--ink)',
  textWrap: 'balance',
};

const HowItWorks: React.FC = () => (
  <>
    <SiteNav />

    {/* HERO */}
    <section style={{ background: 'var(--bg)', padding: '4.25rem 1.5rem 4rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '3rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>How it works</span>
        </Reveal>

        <Reveal
          as="h1"
          style={{
            margin: 0,
            fontWeight: 500,
            fontSize: 'clamp(2.4rem,4.6vw,3.9rem)',
            lineHeight: 1.02,
            letterSpacing: '-.025em',
            color: 'var(--ink)',
          }}
        >
          <span style={{ display: 'block' }}>From quantum risk,</span>
          <span
            aria-hidden="true"
            style={{
              display: 'grid',
              gap: '.7rem',
              margin: '1.6rem 0',
              overflow: 'hidden',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 'clamp(1.05rem,2vw,1.45rem)',
              letterSpacing: '.16em',
              lineHeight: 1,
              color: 'color-mix(in srgb, var(--ink-3) 48%, transparent)',
            }}
          >
            <span className="qssn-dotted-signal__row">{'· '.repeat(88)}</span>
            <span className="qssn-dotted-signal__row qssn-dotted-signal__row--alt" style={{ marginLeft: '2.2rem' }}>{'· '.repeat(88)}</span>
          </span>
          <span style={{ display: 'block', textAlign: 'right' }}>to a second line of defense.</span>
        </Reveal>
      </div>
    </section>

    {/* WHY NOW (dark) */}
    <section style={{ background: 'var(--dark)', color: 'var(--on-dark)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--dark-line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--on-dark-2)' }}>{'Why now'}</span>
          <span style={{ ...microEyebrow, color: 'var(--on-dark-2)' }}>01</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3.5rem 5rem', alignItems: 'flex-start' }}>
          <div style={{ flex: '1 1 460px' }}>
            <Reveal as="h2" style={{ ...h2Style, color: 'var(--on-dark)' } as React.CSSProperties}>
              QSSN helps institutions address quantum risk as planned infrastructure work, before it becomes urgent.
            </Reveal>
            <Reveal as="p" delay={80} style={{ margin: '2rem 0 0', maxWidth: '38rem', fontSize: '1.125rem', lineHeight: 1.6, color: 'var(--on-dark-2)' }}>
              Reducing exposure means prioritizing the actions that carry the most value, then hardening authorization paths where long-lived authority could be exposed to future quantum-capable attacks.
            </Reveal>
          </div>
          <Reveal delay={120} style={{ flex: '1 1 360px', borderLeft: '1px solid var(--dark-line)', paddingLeft: '2rem' }}>
            <div style={{ ...microEyebrow, color: 'var(--accent-on-dark)' }}>Planning posture</div>
            <p style={{ margin: '.75rem 0 0', fontSize: '1.375rem', fontWeight: 500, lineHeight: 1.3, color: 'var(--on-dark)' }}>
              Treat quantum readiness as control hardening, not a future migration date.
            </p>
            <div style={{ marginTop: '2.25rem' }}>
              {WHY_NOW.map((w) => (
                <div key={w.n} style={{ display: 'grid', gridTemplateColumns: '2.5rem 1fr', gap: '1.25rem', padding: '1.4rem 0', borderTop: '1px solid var(--dark-line)' }}>
                  <span style={{ ...mono, fontSize: '.75rem', color: 'var(--on-dark-2)' }}>{w.n}</span>
                  <div>
                    <div style={{ ...mono, fontSize: '.6875rem', letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--accent-on-dark)' }}>{w.kicker}</div>
                    <p style={{ margin: '.6rem 0 0', lineHeight: 1.55, color: 'var(--on-dark-2)' }}>{w.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* THE MECHANISM */}
    <section style={{ background: 'var(--bg)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>{'The mechanism'}</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>02 / Logic</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3rem 4.5rem' }}>
          <div style={{ flex: '1 1 360px' }}>
            <Reveal as="h2" style={h2Style as React.CSSProperties}>
              A second, quantum-safe approval on your most sensitive actions.
            </Reveal>
            <Reveal as="p" delay={80} style={{ margin: '1.75rem 0 0', maxWidth: '32rem', fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
              No chain update required. QSSN sits over your existing operations and requires a quantum-safe approval before any privileged action executes.
            </Reveal>
          </div>
          <Reveal style={{ flex: '1 1 460px' }}>
            {STEPS.map((s) => (
              <div key={s.n} style={{ display: 'grid', gridTemplateColumns: '4rem 1fr', gap: '1.5rem', padding: '1.9rem 0', borderTop: '1px solid var(--line)' }}>
                <span style={{ ...mono, fontSize: '.875rem', color: 'var(--accent)' }}>{s.n}</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 500, lineHeight: 1.2, color: 'var(--ink)', letterSpacing: '-.01em' }}>{s.title}</h3>
                  <p style={{ margin: '.85rem 0 0', lineHeight: 1.6, color: 'var(--ink-2)' }}>{s.body}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </div>
    </section>

    {/* ARCHITECTURE */}
    <section style={{ background: 'var(--bg-alt)', padding: '7rem 1.5rem' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <Reveal
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid var(--line)',
            paddingTop: '1.25rem',
            marginBottom: '3.5rem',
          }}
        >
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>{'System diagram'}</span>
          <span style={{ ...microEyebrow, color: 'var(--ink-3)' }}>03 / Architecture</span>
        </Reveal>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem 4rem', alignItems: 'flex-end', marginBottom: '3rem' }}>
          <Reveal as="h2" style={{ ...h2Style, flex: '1 1 420px' } as React.CSSProperties}>
            Where QSSN sits.
            <br />
            And what stays untouched.
          </Reveal>
          <Reveal as="p" style={{ flex: '1 1 380px', margin: 0, fontSize: '1.0625rem', lineHeight: 1.6, color: 'var(--ink-2)' }}>
            The QSSN node and smart account sit between issuer operations and counterparties: dual authorization, one-time operator keys, preserved controls.
          </Reveal>
        </div>

        <Reveal style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.25rem', ...mono, fontSize: '.625rem', letterSpacing: '.18em', textTransform: 'uppercase', color: 'var(--ink-3)' }}>
          <span style={{ whiteSpace: 'nowrap' }}>Issuer operations</span>
          <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg,var(--accent),var(--line))' }} />
          <span style={{ whiteSpace: 'nowrap' }}>Counterparties</span>
        </Reveal>

        <Reveal style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: 1, background: 'var(--line)', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden' }}>
          {FLOW.map((f) => (
            <div
              key={f.idx}
              style={{
                background: 'var(--surface)',
                padding: '1.6rem 1.5rem',
                minHeight: 200,
                display: 'flex',
                flexDirection: 'column',
                boxShadow: f.active ? 'inset 0 2px 0 var(--accent)' : 'none',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ ...mono, fontSize: '.6rem', letterSpacing: '.14em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: f.active ? 'var(--accent)' : 'var(--ink-3)' }}>{f.idx}</span>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: f.active ? 'var(--accent)' : 'var(--ink-3)' }} />
              </div>
              <h3 style={{ margin: '1.4rem 0 0', fontSize: '1.375rem', fontWeight: 500, color: 'var(--ink)', letterSpacing: '-.01em' }}>{f.title}</h3>
              <p style={{ margin: '.6rem 0 0', fontSize: '.9375rem', lineHeight: 1.5, color: 'var(--ink-2)' }}>{f.body}</p>
              <span style={{ marginTop: 'auto', paddingTop: '1.1rem', ...mono, fontSize: '.625rem', letterSpacing: '.1em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--accent)' }}>{f.foot}</span>
            </div>
          ))}
        </Reveal>

      </div>
    </section>

    <Footer />
  </>
);

export default HowItWorks;
